import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Plus, Play, FileCode2, Clock, ChevronDown, CheckCircle2, XCircle } from 'lucide-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import Editor, { type OnMount } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import { apiClient } from '@/api/client';
import { useMobileStore } from '@/stores/mobile-store';
import { useAppStore } from '@/stores/app-store';
import { useSettingsStore } from '@/stores/settings-store';
import { EDITOR_THEMES } from '@/config/themes';
import { getEditorLanguage } from '@/lib/driver-kind';
import { MobileCard } from '@/components/mobile/shared/MobileCard';
import { NestedFieldNode } from './RecordDetailScreen';
import { SkeletonText } from '@/components/shared/Skeleton';
import type { SavedQuery, QueryResult, QueryHistoryEntry, ConnectionConfig, QueryColumn, DatabaseInfo } from '@/types';

// ═══════════════════════════════════════════
// Server raw types (snake_case from SQLite)
// ═══════════════════════════════════════════

interface RawHistoryRow {
  id: string;
  connection_id: string;
  sql: string;
  status: 'success' | 'error';
  duration_ms: number;
  row_count: number | null;
  error: string | null;
  executed_at: string;
}

function mapHistoryRow(raw: RawHistoryRow): QueryHistoryEntry {
  return {
    id: raw.id,
    connectionId: raw.connection_id,
    sql: raw.sql,
    status: raw.status,
    durationMs: raw.duration_ms,
    rowCount: raw.row_count,
    error: raw.error,
    executedAt: raw.executed_at,
  };
}

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return ts;
  }
}

function truncateSql(sql: string, maxLen = 60): string {
  const oneLine = sql.replace(/\s+/g, ' ').trim();
  if (oneLine.length <= maxLen) return oneLine;
  return oneLine.slice(0, maxLen) + '...';
}

// ═══════════════════════════════════════════
// Query List (root screen)
// ═══════════════════════════════════════════

export function QueryListScreen() {
  const pushScreen = useMobileStore((s) => s.pushScreen);

  const { data: savedQueries = [], isLoading: loadingSaved } = useQuery<SavedQuery[]>({
    queryKey: ['savedQueries'],
    queryFn: () => apiClient.get<SavedQuery[]>('/api/saved-queries'),
  });

  const { data: connections = [] } = useQuery<ConnectionConfig[]>({
    queryKey: ['connections'],
    queryFn: () => apiClient.get<ConnectionConfig[]>('/api/connections'),
  });

  const { data: history = [], isLoading: loadingHistory } = useQuery<QueryHistoryEntry[]>({
    queryKey: ['queryHistory'],
    queryFn: async () => {
      const raw = await apiClient.get<RawHistoryRow[]>('/api/query/history');
      return raw.map(mapHistoryRow);
    },
  });

  const connectionMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of connections) {
      map.set(c.id, c.name);
    }
    return map;
  }, [connections]);

  const handleNewQuery = useCallback(() => {
    pushScreen({
      type: 'query-editor',
      title: 'Query Editor',
      params: { sql: '', connectionId: connections[0]?.id ?? '' },
    });
  }, [pushScreen, connections]);

  const handleOpenSaved = useCallback(
    (q: SavedQuery) => {
      pushScreen({
        type: 'query-editor',
        title: q.title,
        params: { sql: q.sql, connectionId: q.connectionId, savedQueryId: q.id },
      });
    },
    [pushScreen],
  );

  const handleOpenHistory = useCallback(
    (entry: QueryHistoryEntry) => {
      pushScreen({
        type: 'query-editor',
        title: 'Query Editor',
        params: { sql: entry.sql, connectionId: entry.connectionId },
      });
    },
    [pushScreen],
  );

  const recentHistory = history.slice(0, 20);

  return (
    <div className="h-full overflow-y-auto">
      {/* New Query button */}
      <div className="px-4 pt-3 pb-2">
        <button
          onClick={handleNewQuery}
          className="w-full flex items-center justify-center gap-2 rounded-lg text-sm font-medium text-white"
          style={{
            height: 44,
            backgroundColor: 'var(--accent-blue)',
          }}
        >
          <Plus size={18} />
          New Query
        </button>
      </div>

      {/* Saved Queries */}
      <div className="px-4 pt-2">
        <div className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
          Saved Queries
        </div>
        {loadingSaved && <SkeletonText lines={3} />}
        {!loadingSaved && savedQueries.length === 0 && (
          <div className="text-sm text-text-muted py-4 text-center">
            No saved queries yet
          </div>
        )}
        <div className="space-y-2">
          {savedQueries.map((q) => (
            <button
              key={q.id}
              onClick={() => handleOpenSaved(q)}
              className="w-full text-left rounded-lg border px-3 py-2.5 active:bg-dbgo-tab-hover transition-colors"
              style={{
                backgroundColor: 'var(--dbgo-panel)',
                borderColor: 'var(--border-default)',
              }}
            >
              <div className="flex items-center gap-2">
                <FileCode2 size={16} className="text-accent-blue flex-shrink-0" />
                <span className="text-sm text-text-primary truncate">{q.title}</span>
              </div>
              <div className="text-xs text-text-muted mt-0.5 ml-6">
                {connectionMap.get(q.connectionId) ?? 'No connection'}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Recent History */}
      <div className="px-4 pt-4 pb-4">
        <div className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
          Recent
        </div>
        {loadingHistory && <SkeletonText lines={3} />}
        {!loadingHistory && recentHistory.length === 0 && (
          <div className="text-sm text-text-muted py-4 text-center">
            No query history yet
          </div>
        )}
        <div className="space-y-1">
          {recentHistory.map((entry) => (
            <button
              key={entry.id}
              onClick={() => handleOpenHistory(entry)}
              className="w-full text-left flex items-center gap-2 py-2 px-2 rounded-md active:bg-dbgo-tab-hover transition-colors"
            >
              <div className="flex-shrink-0">
                {entry.status === 'success' ? (
                  <CheckCircle2 size={14} className="text-accent-green" />
                ) : (
                  <XCircle size={14} className="text-accent-red" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs text-text-secondary truncate font-mono">
                  {truncateSql(entry.sql)}
                </div>
                <div className="text-[10px] text-text-muted flex items-center gap-2 mt-0.5">
                  <Clock size={10} />
                  {formatTimestamp(entry.executedAt)}
                  <span className="text-accent-green">{entry.durationMs}ms</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// Query Editor (pushed screen)
// ═══════════════════════════════════════════

export function QueryEditorScreen({ params }: { params: Record<string, unknown> }) {
  const initialSql = (params.sql as string) ?? '';
  const initialConnectionId = (params.connectionId as string) ?? '';
  const pushScreen = useMobileStore((s) => s.pushScreen);

  const [sql, setSql] = useState(initialSql);
  const [connectionId, setConnectionId] = useState(initialConnectionId);
  const [database, setDatabase] = useState<string | undefined>(undefined);
  const [result, setResult] = useState<QueryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showConnectionPicker, setShowConnectionPicker] = useState(false);
  const [showDatabasePicker, setShowDatabasePicker] = useState(false);
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);

  const { data: connections = [] } = useQuery<ConnectionConfig[]>({
    queryKey: ['connections'],
    queryFn: () => apiClient.get<ConnectionConfig[]>('/api/connections'),
  });

  // Pick current connection name
  const currentConnection = connections.find((c) => c.id === connectionId);

  // Databases for the active connection. Cache-first server-side, so
  // the bottom-sheet opens with whatever's known without round-tripping.
  const { data: databases = [] } = useQuery<DatabaseInfo[]>({
    queryKey: ['databases', connectionId],
    queryFn: () => apiClient.get<DatabaseInfo[]>(`/api/connections/${connectionId}/databases`),
    enabled: !!connectionId,
    staleTime: 60_000,
  });
  // Active DB: explicit override → connection default → first listed.
  // The chip label flips to "(default)" so users see when they haven't
  // drifted from the connection's home DB.
  const activeDatabase = database ?? currentConnection?.databaseName ?? '';
  const isOverridingDefault = !!database && database !== currentConnection?.databaseName;
  // Driver kind flips Monaco's language so Mongo connections get JS
  // highlighting + the mongosh-aware autocomplete provider that the
  // desktop QueryEditor registers globally.
  const editorLanguage = getEditorLanguage(currentConnection?.driverType ?? 'postgresql');

  // Monaco theme
  const theme = useAppStore((s) => s.theme);
  const ss = useSettingsStore.getState();
  const editorThemeKey = theme === 'dark' ? 'editor.theme.dark' : 'editor.theme.light';
  const editorThemeDefault = theme === 'dark' ? 'dbgo-dark' : 'dbgo-light';
  const editorThemeId = ss.get(editorThemeKey, editorThemeDefault);
  const editorTheme = EDITOR_THEMES.find((t) => t.id === editorThemeId) ?? EDITOR_THEMES[0];

  const handleEditorMount: OnMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    // Register custom theme
    monaco.editor.defineTheme('custom-theme', editorTheme.monaco);
    monaco.editor.setTheme('custom-theme');
  }, [editorTheme.monaco]);

  const executeMutation = useMutation({
    mutationFn: async () => {
      if (!connectionId || !sql.trim()) {
        throw new Error('Select a connection and enter a query');
      }
      return apiClient.post<QueryResult>('/api/query/execute', {
        connectionId,
        database,
        sql: sql.trim(),
      });
    },
    onSuccess: (data) => {
      setResult(data);
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message);
      setResult(null);
    },
  });

  const handleRun = useCallback(() => {
    executeMutation.mutate();
  }, [executeMutation]);

  const handleRowTap = useCallback(
    (row: Record<string, unknown>, columns: QueryColumn[]) => {
      pushScreen({
        type: 'query-record-detail',
        title: 'Record',
        params: { row, columns },
      });
    },
    [pushScreen],
  );

  return (
    <div className="h-full flex flex-col">
      {/* Connection selector */}
      <div className="px-3 py-2 border-b shrink-0" style={{ borderColor: 'var(--border-default)' }}>
        <div className="relative">
          <button
            onClick={() => setShowConnectionPicker(!showConnectionPicker)}
            className="w-full flex items-center justify-between rounded-md border px-3 text-sm"
            style={{
              height: 36,
              backgroundColor: 'var(--dbgo-input)',
              borderColor: 'var(--border-default)',
            }}
          >
            <span className="truncate text-text-primary">
              {currentConnection?.name ?? 'Select connection'}
            </span>
            <ChevronDown size={16} className="text-text-muted flex-shrink-0" />
          </button>
          {showConnectionPicker && (
            <div
              className="absolute top-full left-0 right-0 mt-1 rounded-lg border shadow-lg z-10 max-h-60 overflow-y-auto"
              style={{
                backgroundColor: 'var(--dbgo-panel)',
                borderColor: 'var(--border-default)',
              }}
            >
              {connections.map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    setConnectionId(c.id);
                    // Clear the DB override on connection switch — picking
                    // a new connection should land on its own default.
                    setDatabase(undefined);
                    setShowConnectionPicker(false);
                  }}
                  className="w-full text-left px-3 py-2.5 text-sm text-text-primary active:bg-dbgo-tab-hover"
                >
                  {c.name}
                </button>
              ))}
              {connections.length === 0 && (
                <div className="px-3 py-3 text-sm text-text-muted text-center">
                  No connections
                </div>
              )}
            </div>
          )}
        </div>

        {/* Active-database chip. Shows the DB queries actually run
            against — defaults to the connection's home DB, switches to
            a yellow tint when the user has overridden it so they
            notice they've drifted. Tap to pick another from the list. */}
        {connectionId && databases.length > 1 && (
          <button
            onClick={() => setShowDatabasePicker(true)}
            className="mt-2 w-full flex items-center justify-between rounded-md border px-3 text-sm"
            style={{
              height: 32,
              backgroundColor: 'var(--dbgo-input)',
              borderColor: 'var(--border-default)',
            }}
          >
            <span className="flex items-center gap-2 truncate">
              <span className="text-xs text-text-muted">DB</span>
              <span className={`font-mono truncate ${isOverridingDefault ? 'text-accent-yellow' : 'text-text-primary'}`}>
                {activeDatabase || '(default)'}
              </span>
            </span>
            <ChevronDown size={14} className="text-text-muted flex-shrink-0" />
          </button>
        )}
      </div>

      {/* Monaco editor (35-40% of remaining height) */}
      <div className="shrink-0 border-b" style={{ height: '35%', borderColor: 'var(--border-default)' }}>
        <Editor
          language={editorLanguage}
          value={sql}
          onChange={(v) => setSql(v ?? '')}
          onMount={handleEditorMount}
          theme="custom-theme"
          options={{
            fontSize: 14,
            minimap: { enabled: false },
            wordWrap: 'on',
            lineNumbers: 'off',
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: 2,
            renderLineHighlight: 'none',
            overviewRulerLanes: 0,
            hideCursorInOverviewRuler: true,
            scrollbar: {
              vertical: 'auto',
              horizontal: 'hidden',
            },
            padding: { top: 8, bottom: 8 },
          }}
        />
      </div>

      {/* Run button */}
      <div className="px-3 py-2 shrink-0 flex items-center justify-between">
        <button
          onClick={handleRun}
          disabled={executeMutation.isPending || !connectionId}
          className="flex items-center gap-2 rounded-lg px-4 text-sm font-medium text-white disabled:opacity-50"
          style={{
            height: 36,
            backgroundColor: 'var(--accent-green)',
          }}
        >
          <Play size={14} fill="currentColor" />
          {executeMutation.isPending ? 'Running...' : 'Run'}
        </button>
        {result && (
          <span className="text-xs text-text-muted">
            {result.rowCount} row{result.rowCount !== 1 ? 's' : ''} &middot; {result.durationMs}ms
          </span>
        )}
      </div>

      {/* Results */}
      <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-4">
        {error && (
          <div
            className="rounded-lg border px-3 py-2.5 text-sm"
            style={{
              borderColor: 'var(--accent-red)',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: 'var(--accent-red)',
            }}
          >
            {error}
          </div>
        )}

        {executeMutation.isPending && (
          <div className="py-8 text-center text-sm text-text-muted">
            Executing query...
          </div>
        )}

        {result && result.rows.length === 0 && (
          <div className="py-8 text-center text-sm text-text-muted">
            Query returned no rows
          </div>
        )}

        {result && result.rows.length > 0 && (
          <div className="space-y-2">
            <div className="text-xs text-text-muted font-semibold uppercase tracking-wider">
              Results ({result.rowCount} row{result.rowCount !== 1 ? 's' : ''})
            </div>
            {result.rows.map((row, i) => (
              <MobileCard
                key={i}
                fields={result.columns.map((col) => ({
                  name: col.name,
                  value: row[col.name],
                  type: col.type,
                  isPK: col.isPrimaryKey,
                }))}
                onTap={() => handleRowTap(row, result.columns)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Database picker bottom sheet */}
      {showDatabasePicker && (
        <div
          className="fixed inset-0 z-50 flex items-end"
          onClick={() => setShowDatabasePicker(false)}
        >
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="relative w-full rounded-t-xl p-3 max-h-[60vh] overflow-y-auto"
            style={{ backgroundColor: 'var(--dbgo-panel)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pb-2">
              <div className="rounded-full" style={{ width: 40, height: 4, backgroundColor: 'var(--border-strong)' }} />
            </div>
            <div className="text-xs text-text-muted px-1 mb-1">Run against</div>
            <div className="space-y-1">
              {databases.map((db) => {
                const isActive = db.name === activeDatabase;
                const isConnDefault = db.name === currentConnection?.databaseName;
                return (
                  <button
                    key={db.name}
                    onClick={() => {
                      // Picking the connection's default clears the
                      // override so the tab tracks the default again.
                      setDatabase(isConnDefault ? undefined : db.name);
                      setShowDatabasePicker(false);
                    }}
                    className="w-full flex items-center justify-between rounded-md px-3 py-2.5 text-sm active:bg-dbgo-tab-hover"
                    style={{ backgroundColor: isActive ? 'var(--dbgo-tab-hover)' : 'transparent' }}
                  >
                    <span className={`font-mono truncate ${isActive ? 'text-accent-blue' : 'text-text-primary'}`}>
                      {db.name}
                    </span>
                    <span className="flex items-center gap-2 flex-shrink-0">
                      {isConnDefault && <span className="text-xs text-text-muted">default</span>}
                      {isActive && <CheckCircle2 size={14} className="text-accent-blue" />}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
// Query Record Detail (pushed from results)
// ═══════════════════════════════════════════

export function QueryRecordDetailScreen({ params }: { params: Record<string, unknown> }) {
  const row = params.row as Record<string, unknown>;
  const columns = params.columns as QueryColumn[];

  return (
    <div className="h-full overflow-y-auto px-4 py-3 space-y-4">
      {columns.map((col) => {
        const value = row[col.name];
        const isNested = value !== null && value !== undefined && typeof value === 'object';
        if (isNested) {
          return <NestedFieldNode key={col.name} label={col.name} type={col.type} value={value} />;
        }
        const display = value === null || value === undefined ? 'NULL' : String(value);
        return (
          <div key={col.name}>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-medium text-text-muted">{col.name}</span>
              <span className="text-[10px] text-text-muted">{col.type}</span>
            </div>
            <div
              className="w-full rounded-lg px-3 flex items-center text-sm border"
              style={{
                minHeight: 36,
                paddingTop: 8,
                paddingBottom: 8,
                backgroundColor: 'var(--dbgo-input)',
                borderColor: 'var(--border-default)',
                color: value === null || value === undefined ? 'var(--text-muted)' : 'var(--text-primary)',
                fontStyle: value === null || value === undefined ? 'italic' : 'normal',
              }}
            >
              <span className="truncate">{display}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
